import React from 'react';
import { Stack } from '@grnhse/seedling/lib/birch/layouts/stack_v2';
import { IconButton } from '@grnhse/seedling/lib/birch/components/buttons';
import { Body } from '@grnhse/seedling/lib/birch/typography';
import DownloadIcon from '@grnhse/seedling/lib/birch/icons/download';
import CloseIcon from '@grnhse/seedling/lib/birch/icons/close';
import DocumentIcon from 'shared/components/documents/document_icon';
import { withLocalePrefix } from 'shared/utils/translation';
import { Document } from 'talent_matching/types';
import styled from 'styled-components';
import Flash from 'legacy/flash';
import { Person } from 'talent_matching/types';
import {
  SecondaryButton,
  SecondaryButtonExternalLink,
  DangerButton,
} from '@grnhse/seedling/lib/birch/components/buttons';
import AdvanceButton from 'talent_matching/components/shared/advance_button';
import Bookmark from 'talent_matching/components/shared/bookmark';

import { useUserContext } from 'talent_matching/contexts/user_context';

const t = withLocalePrefix('talent_matching');

const TruncatedBody = styled(Body)`
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  margin-right: 24px;
  max-width: 245px;
  min-width: 0;
`;

export type DocumentHeaderProps = {
  document: Document;
  onClose?: () => void;
  candidate: Person;
  openRejectModal?: () => void;
  openFeedbackModal?: () => void;
  canAdvance?: boolean;
  canReject?: boolean;
  canDownloadCandidateData?: boolean;
  showCandidateActions?: boolean;
  onAdvanceSuccess?: () => void;
};

const NoWrapSecondaryButton = styled(SecondaryButton)`
  white-space: nowrap;
`;

const NoWrapSecondaryButtonExternalLink = styled(SecondaryButtonExternalLink)`
  white-space: nowrap;
`;

const DocumentHeader: React.FC<DocumentHeaderProps> = ({
  document,
  onClose,
  candidate,
  openRejectModal,
  openFeedbackModal,
  canAdvance,
  canReject,
  canDownloadCandidateData,
  showCandidateActions = true,
  onAdvanceSuccess,
}) => {
  const { linkedinRscEnabled } = useUserContext();

  const isAnonymized = document.type === 'anonymized_resume';

  const handleDownload = React.useCallback(() => {
    if (!document.downloadUrl) {
      Flash.setError(t('document_header.download_error_no_url'));
      return;
    }
    window.open(document.downloadUrl, '_blank');
  }, [document]);

  const fileName = isAnonymized ? t('anonymized.resume') : document.file_name;
  const showDownloadButton = canDownloadCandidateData && !isAnonymized;

  return (
    <Stack
      direction="row"
      justifyContent="space-between"
      alignItems="center"
      sx={{
        paddingBottom: onClose ? 3 : 1,
        paddingTop: onClose ? 0 : 1,
        width: '100%',
      }}
    >
      <Stack
        direction="row"
        spacing={1}
        alignItems="center"
        sx={{ minWidth: 0, flex: '1 1 auto' }}
      >
        {!isAnonymized && (
          <DocumentIcon size="md" extension={document.extension} />
        )}
        <TruncatedBody title={fileName} className="pii-blur">
          {fileName}
        </TruncatedBody>
      </Stack>
      <Stack
        direction="row"
        spacing={2}
        alignItems="center"
        sx={{ flexShrink: 0 }}
      >
        {onClose && showCandidateActions && (
          <>
            <Stack direction="row" spacing={2} alignItems="center">
              <AdvanceButton
                application={candidate}
                canAdvance={canAdvance}
                onSuccess={onAdvanceSuccess}
              />
              <DangerButton
                onClick={openRejectModal}
                size="md"
                disabled={!canReject}
              >
                {t('reject')}
              </DangerButton>
              <NoWrapSecondaryButton onClick={openFeedbackModal} size="md">
                {t('leave_feedback')}
              </NoWrapSecondaryButton>
            </Stack>
            <Bookmark application={candidate} size="md" isFlat={false} />
          </>
        )}
        {!linkedinRscEnabled && candidate.linkedinAddress && (
          <NoWrapSecondaryButtonExternalLink
            href={candidate.linkedinAddress}
            size="md"
          >
            {t('open_linkedin')}
          </NoWrapSecondaryButtonExternalLink>
        )}
        {showDownloadButton && (
          <IconButton
            icon={DownloadIcon}
            label={t('actions.download')}
            onClick={handleDownload}
            size="md"
          />
        )}
        {onClose && (
          <IconButton
            icon={CloseIcon}
            label={t('actions.close_panel')}
            onClick={onClose}
            size="md"
          />
        )}
      </Stack>
    </Stack>
  );
};

export default DocumentHeader;
